import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';

import type { ForecastReviewAlert } from '@/services/forecastReviewService';
import { getForecastAlertBusinessMessage } from '../utils/forecastAlertDisplay';

interface AlertRejectModalProps {
  open: boolean;
  alert: ForecastReviewAlert | null;
  loading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function AlertRejectModal({
  open,
  alert,
  loading,
  onConfirm,
  onClose,
}: AlertRejectModalProps) {
  const { t } = useTranslation();

  if (!open || !alert) return null;

  const content = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={loading ? undefined : onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="alert-reject-modal-title"
        className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id="alert-reject-modal-title" className="text-base font-semibold text-gray-900">
              {t('forecastReview.alerts.rejectModal.title')}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {alert.element_name ?? alert.element_code ?? `#${alert.id}`}
            </p>
          </div>
          {!loading ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label={t('forecastReview.alerts.rejectModal.close')}
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        <div className="px-5 py-4">
          <p className="text-sm text-gray-600">{t('forecastReview.alerts.rejectModal.message')}</p>
          <p className="mt-2 text-sm text-gray-500">{getForecastAlertBusinessMessage(alert, t)}</p>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <button
            type="button"
            className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
            onClick={onClose}
            disabled={loading}
          >
            {t('forecastReview.alerts.rejectModal.cancel')}
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            onClick={() => void onConfirm()}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('forecastReview.alerts.rejectModal.confirm')}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
